"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { usePathname, useSearchParams } from "next/navigation";

import styles from "./AdSlot.module.css";
import { AD_ARIA_LABEL, AD_LABEL_VISIBLE, ADS_CLIENT, hasAdSlot } from "./config";
import { ADSENSE_INLINE_ONLY } from "@/components/nitro/config";

type AdVariant = "hero" | "inline" | "rectangle";
type AdPlacement = "fullBleed" | "content" | "compact";

type AdSlotProps = {
  slot: string;
  contextKey: string;
  variant?: AdVariant;
  placement?: AdPlacement;
  className?: string;
  showLabel?: boolean;
  threshold?: number;
  rootMargin?: string;
};

type FixedAdSize = {
  width: number;
  height: number;
};

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    __bfcAdConsentGranted?: boolean;
    __bfcAdsBootReady?: boolean;
  }
}

const IS_PROD = process.env.NODE_ENV === "production";
const EMPTY_COLLAPSE_DELAY_MS = 15000;

function getFixedSize(variant: AdVariant, viewportWidth: number): FixedAdSize {
  if (variant === "rectangle") {
    if (viewportWidth < 360) {
      return { width: 300, height: 250 };
    }

    return { width: 336, height: 280 };
  }

  if (variant === "hero") {
    if (viewportWidth >= 1280) {
      return { width: 970, height: 250 };
    }

    if (viewportWidth >= 768) {
      return { width: 728, height: 90 };
    }

    return { width: 320, height: 100 };
  }

  if (viewportWidth >= 768) {
    return { width: 728, height: 90 };
  }

  return { width: 320, height: 100 };
}

function slotLooksFilled(node: HTMLElement | null): boolean {
  if (!node) return false;

  const adStatus = node.getAttribute("data-ad-status");
  if (adStatus === "filled") return true;
  if (adStatus === "unfilled") return false;

  if (node.querySelector("iframe, img, video, ins.adsbygoogle-noablate")) {
    return true;
  }

  const text = node.textContent?.trim();
  return Boolean(text && text.length > 0);
}

function slotIsProcessed(node: HTMLElement | null): boolean {
  if (!node) return false;

  return (
    node.getAttribute("data-adsbygoogle-status") === "done" ||
    node.hasAttribute("data-ad-status") ||
    slotLooksFilled(node)
  );
}

export default function AdSlot({
  slot,
  contextKey,
  variant = "inline",
  placement = "content",
  className,
  showLabel = true,
  threshold = 0.1,
  rootMargin = "320px 0px",
}: AdSlotProps) {
  const pathname = usePathname() ?? "";
  const searchParams = useSearchParams();
  const routeKey = `${pathname}?${searchParams?.toString() ?? ""}`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const insRef = useRef<HTMLElement | null>(null);
  const hasRequestedInitRef = useRef(false);

  const [isVisible, setIsVisible] = useState(!IS_PROD);
  const [shouldRenderIns, setShouldRenderIns] = useState(!IS_PROD);
  const [isFilled, setIsFilled] = useState(!IS_PROD);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [hasConsent, setHasConsent] = useState(!IS_PROD);
  const [bootReady, setBootReady] = useState(!IS_PROD);
  const [size, setSize] = useState<FixedAdSize>(() => getFixedSize(variant, 1440));

  const shouldUseLiveAds = IS_PROD && !ADSENSE_INLINE_ONLY && Boolean(ADS_CLIENT) && hasAdSlot(slot);

  const instanceKey = useMemo(
    () => `${contextKey}:${routeKey}:${slot}:${size.width}x${size.height}`,
    [contextKey, routeKey, slot, size.height, size.width]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateSize = () => {
      setSize(getFixedSize(variant, window.innerWidth || 1440));
    };

    updateSize();
    window.addEventListener("resize", updateSize, { passive: true });

    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, [variant]);

  useEffect(() => {
    hasRequestedInitRef.current = false;
    setIsVisible(!IS_PROD);
    setShouldRenderIns(!IS_PROD);
    setIsFilled(!IS_PROD);
    setHasTimedOut(false);
    setHasConsent(!IS_PROD || window.__bfcAdConsentGranted === true);
    setBootReady(!IS_PROD || window.__bfcAdsBootReady === true);
  }, [instanceKey]);

  useEffect(() => {
    if (!IS_PROD) return undefined;

    const syncFlags = () => {
      setHasConsent(window.__bfcAdConsentGranted === true);
      setBootReady(window.__bfcAdsBootReady === true);
    };

    syncFlags();
    window.addEventListener("bfc:ads:ready", syncFlags);
    window.addEventListener("bfc:consent:updated", syncFlags);

    return () => {
      window.removeEventListener("bfc:ads:ready", syncFlags);
      window.removeEventListener("bfc:consent:updated", syncFlags);
    };
  }, []);

  useEffect(() => {
    if (!IS_PROD || !shouldUseLiveAds || !rootRef.current) return undefined;

    const node = rootRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [instanceKey, rootMargin, shouldUseLiveAds, threshold]);

  useEffect(() => {
    if (!IS_PROD) return undefined;
    if (!shouldUseLiveAds) return undefined;
    if (!isVisible) return undefined;
    if (!hasConsent) return undefined;
    if (!bootReady) return undefined;
    if (hasRequestedInitRef.current) return undefined;

    hasRequestedInitRef.current = true;
    setShouldRenderIns(true);

    const id = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("bfc:ads:refresh", {
          detail: { routeKey, slot, contextKey, instanceKey },
        })
      );
    }, 80);

    return () => window.clearTimeout(id);
  }, [
    bootReady,
    contextKey,
    hasConsent,
    instanceKey,
    isVisible,
    routeKey,
    shouldUseLiveAds,
    slot,
  ]);

  useEffect(() => {
    if (!IS_PROD || !shouldUseLiveAds || !shouldRenderIns || !hasConsent || !bootReady) {
      return undefined;
    }

    const node = insRef.current;
    if (!node) return undefined;

    let collapseTimer: number | null = null;

    const clearCollapseTimer = () => {
      if (collapseTimer !== null) {
        window.clearTimeout(collapseTimer);
        collapseTimer = null;
      }
    };

    const evaluate = () => {
      const filled = slotLooksFilled(node);
      const processed = slotIsProcessed(node);

      if (filled) {
        setIsFilled(true);
        setHasTimedOut(false);
        clearCollapseTimer();
        return;
      }

      if (!processed) return;

      clearCollapseTimer();
      collapseTimer = window.setTimeout(() => {
        if (!slotLooksFilled(node)) {
          setIsFilled(false);
          setHasTimedOut(true);
        }
      }, EMPTY_COLLAPSE_DELAY_MS);
    };

    const observer = new MutationObserver(evaluate);
    observer.observe(node, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    evaluate();

    return () => {
      observer.disconnect();
      clearCollapseTimer();
    };
  }, [bootReady, hasConsent, instanceKey, shouldRenderIns, shouldUseLiveAds]);

  const showDevSlot = !IS_PROD;
  const renderLiveSlot = shouldUseLiveAds && shouldRenderIns && hasConsent;
  const showPlaceholder = !showDevSlot && (!renderLiveSlot || !isFilled);

  const rootClassName = [styles.root, className].filter(Boolean).join(" ");

  const style = {
    "--ad-width": `${size.width}px`,
    "--ad-height": `${size.height}px`,
    "--ad-max-width": `${size.width}px`,
  } as CSSProperties;

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      style={style}
      data-ad-slot-root="true"
      data-ad-variant={variant}
      data-ad-placement={placement}
      data-ad-context={instanceKey}
      data-ad-mounted={renderLiveSlot ? "true" : "false"}
      data-ad-filled={isFilled ? "true" : "false"}
      data-ad-timeout={hasTimedOut ? "true" : "false"}
      aria-hidden={!renderLiveSlot && !showDevSlot ? "true" : undefined}
    >
      {(showDevSlot || renderLiveSlot) && showLabel && AD_LABEL_VISIBLE ? (
        <div className={styles.labelRow} aria-hidden="true">
          <span className={styles.label}>{AD_LABEL_VISIBLE}</span>
        </div>
      ) : null}

      <div className={styles.outer}>
        <div className={styles.shell}>
          <div className={styles.slot}>
            {showDevSlot ? (
              <div className={styles.devSlot}>
                <span className={styles.devText}>AdSlot (dev): {instanceKey}</span>
              </div>
            ) : renderLiveSlot ? (
              <ins
                key={instanceKey}
                ref={(node) => {
                  insRef.current = node;
                }}
                className="adsbygoogle"
                style={{
                  display: "inline-block",
                  width: `${size.width}px`,
                  height: `${size.height}px`,
                }}
                data-ad-managed="boot"
                data-ad-client={ADS_CLIENT}
                data-ad-slot={slot}
                data-full-width-responsive="false"
                data-ad-id={instanceKey}
                data-ad-route={routeKey}
                data-ad-type="display"
                aria-label={AD_ARIA_LABEL}
              />
            ) : null}

            {showPlaceholder ? (
              <div className={styles.placeholder} aria-hidden="true" />
            ) : null}

            {!renderLiveSlot ? <div className={styles.sentinel} aria-hidden="true" /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
